/*
 * Copyright (C) 2023 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MANDANTS} from '@models/mandant';
import {ApplicationPropertyRsService} from '@utils/application-property-rs';
import {StateService} from '@uirouter/core';
import {MandantService} from '@utils/mandant';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthServiceRS} from '../../../../authentication/service/AuthServiceRS.rest';
import {TSRole} from '../../../../models/enums/TSRole';
import {TSRoleUtil} from '../../../../utils/TSRoleUtil';
import {ErrorService} from '../../errors/service/ErrorService';
import {BenutzerRSX} from '../../service/benutzerRSX.rest';
import {DvNgOkDialogComponent} from '../dv-ng-ok-dialog/dv-ng-ok-dialog.component';

@Component({
    selector: 'dv-pulldown-user-menu',
    templateUrl: './pulldown-user-menu.component.html',
    styleUrls: ['./pulldown-user-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PulldownUserMenuComponent implements OnInit {
    private readonly authService = inject(AuthServiceRS);
    private readonly applicationPropertyRS = inject(
        ApplicationPropertyRsService
    );
    private readonly state = inject(StateService);
    private readonly errorService = inject(ErrorService);

    public multimandantAktiv: boolean;
    public frenchEnabled: boolean;
    public testfaelleEnabled: boolean;

    public constructor(
        private readonly mandantService: MandantService,
        private readonly benutzerRS: BenutzerRSX,
        private readonly dvDialog: MatDialog
    ) {}

    public ngOnInit(): void {
        this.initMandantSwitch();
        this.initFrenchEnabled();
        this.initTestFaelleEnabled();
    }

    public getFullName(): Observable<string | undefined> {
        return this.authService.principal$.pipe(
            map(principal => principal?.getFullName())
        );
    }

    public getSuperAdminRoles(): ReadonlyArray<TSRole> {
        return TSRoleUtil.getSuperAdminRoles();
    }

    public getAllAdministratorRevisorRole(): ReadonlyArray<TSRole> {
        return TSRoleUtil.getAllAdministratorRevisorRole();
    }

    public getInstitutionProfilRoles(): ReadonlyArray<TSRole> {
        return TSRoleUtil.getInstitutionProfilRoles();
    }

    public getMandantRoles(): ReadonlyArray<TSRole> {
        return TSRoleUtil.getMandantRoles();
    }

    public getTraegerschaftId(): Observable<string | undefined> {
        return this.authService.principal$.pipe(
            map(principal => principal?.currentBerechtigung?.traegerschaft?.id)
        );
    }

    public getAdministratorBgTsGemeindeOrMandantRole(): ReadonlyArray<TSRole> {
        return TSRoleUtil.getAdministratorBgTsGemeindeOrMandantRole();
    }

    public getAllRolesForSozialdienst(): ReadonlyArray<TSRole> {
        return TSRoleUtil.getAllRolesForSozialdienst();
    }

    private initMandantSwitch(): void {
        this.applicationPropertyRS.isMultimandantEnabled().subscribe(res => {
            this.multimandantAktiv = res;
        });
    }

    private initFrenchEnabled(): void {
        this.applicationPropertyRS.getFrenchEnabled().subscribe(res => {
            this.frenchEnabled = res;
        });
    }

    private initTestFaelleEnabled() {
        this.applicationPropertyRS.isTestfaelleEnabled().subscribe(res => {
            this.testfaelleEnabled = res;
        });
    }

    public isDevMode() {
        return this.applicationPropertyRS.isDevMode();
    }

    /**
     * @returns If the current user is allowed to change his password. User's of mandant Bern
     * aren't allowed to do so, because Bern uses AGOV (external IDP) login, where no password is required.
     */
    public canUpdatePassword(): boolean {
        let mandantIsNotBern: boolean;
        this.mandantService.mandant$.subscribe(mandant => {
            mandantIsNotBern = MANDANTS.BERN !== mandant;
        });

        return mandantIsNotBern;
    }

    public updatePassword(): void {
        this.benutzerRS
            .updatePassword()
            .then(() => {
                this.dvDialog.open(DvNgOkDialogComponent, {
                    data: {
                        title: 'UPDATE_PASSWORD_DIALOG_TITLE',
                        text: 'UPDATE_PASSWORD_DIALOG_TEXT'
                    }
                });
            })
            .catch(error => {
                console.error('Failed to update password:', error);
            });
    }

    public logout(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        this.authService.initLogout().then(logoutResponse => {
            if (!logoutResponse.logoutSuccess) {
                // Fehler anzeigen, dass der Logout nicht funktionert hat.
                this.errorService.addMesageAsError(logoutResponse.message);
            }
        });
    }
}
